import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { Grid } from '@mui/material'
import { makeStyles } from '@mui/styles'
import * as R from 'ramda'
import {
  ButtonWithLoader,
  type ClassesType,
  type Nil,
  Text,
} from '@pbt/pbt-ui-components'

import LetUsKnowLink from '~/components/common/LetUsKnowLink'
import FeatureToggle from '~/constants/featureToggle'
import type { RootState } from '~/store'
import { createInventory } from '~/store/actions/inventories'
import { createVariations } from '~/store/actions/variations'
import { useIsGlobalInventoryItem } from '~/store/hooks/orders'
import {
  getCvcCumulusRhapsodyIntegrationInventoryEnabled,
  getFeatureToggle,
} from '~/store/reducers/constants'
import {
  getInventory,
  getInventoryIsSending,
  getLastCreatedInventory,
} from '~/store/reducers/inventories'
import { getVariationIsSending } from '~/store/reducers/variations'
import type {
  GlobalInventoryCatalogItem,
  InventoryItem,
  Variation as VariationType,
} from '~/types'
import useCloseAfterCreation from '~/utils/useCloseAfterCreation'

import GlobalItemSelector from '../global-selector/GlobalItemSelector'
import {
  addOrUpdateVariation,
  getNextVariationKey,
  getPrevVariationKey,
  getVariation,
  getVariationIndex,
  getVariationKey,
  getVariationsCount,
  insertVariation,
} from '../inventoryUtils'
import CategorySelector from './CategorySelector'
import Inventory, { type InventoryHandle } from './Inventory'
import InventoryRestriction, {
  type InventoryRestrictionHandle,
} from './InventoryRestriction'
import { ReadOnlyVariation } from './read-only-variation/ReadOnlyVariation'
import Variation from './Variation'

const useStyles = makeStyles(
  () => ({
    saveButton: {
      height: 40,
      width: 150,
    },
  }),
  { name: 'AddInventoryItem' },
)

const loadingSelector = (state: RootState) =>
  getInventoryIsSending(state) || getVariationIsSending(state)

interface VariationHandle {
  getIsTouched: () => boolean
  save: () => void
}

export interface AddInventoryItemHandle {
  confirmSave: () => void
  hasUnsavedChanges: () => boolean
}

interface AddInventoryItemProps {
  classes?: ClassesType<typeof useStyles>
  duplicateInventoryId?: string | Nil
  onClose: (inventoryId: string | Nil, inventory?: InventoryItem | Nil) => void
  onVariationFocus?: (variationKey: string | Nil) => void
  singleItemOnly?: boolean
}

const AddInventoryItem = forwardRef<
  AddInventoryItemHandle,
  AddInventoryItemProps
>(function AddInventoryItem(
  {
    duplicateInventoryId,
    onClose,
    onVariationFocus,
    singleItemOnly,
    classes: classesProp,
  },
  ref,
) {
  const classes = useStyles({ classes: classesProp })
  const dispatch = useDispatch()
  const { t } = useTranslation(['Admin', 'Common'])

  const isFoodCatalogEnabled = useSelector(
    getFeatureToggle(FeatureToggle.FOOD_CATALOG),
  )
  const isLoading = useSelector(loadingSelector)
  const originalInventory = useSelector(getInventory(duplicateInventoryId))
  const lastCreatedInventory = useSelector(getLastCreatedInventory)
  const lastCreatedInventoryId = lastCreatedInventory?.id

  const inventorySection = useRef<InventoryHandle>(null)
  const inventoryRestrictionSection = useRef<InventoryRestrictionHandle>(null)
  const variationRef = useRef<VariationHandle>(null)

  const [inventory, setInventory] = useState({} as InventoryItem)
  const [variationsToCreate, setVariationsToCreate] = useState<
    Record<string, VariationType[]>
  >({})
  const [selectedVariationsMap, setSelectedVariationsMap] = useState<
    Record<string, VariationType[]>
  >({})
  const [inventoryToCreate, setInventoryToCreate] = useState<
    GlobalInventoryCatalogItem | InventoryItem | Nil
  >()
  const [currentVariationKey, setCurrentVariationKey] = useState<string | Nil>()
  const [isTouched, setIsTouched] = useState(false)
  const isCvcCumulusRhapsodyIntegrationInventoryEnabled = useSelector(
    getCvcCumulusRhapsodyIntegrationInventoryEnabled,
  )

  const focusOnVariation = useCallback(
    (variationKey: string | Nil) => {
      setCurrentVariationKey(variationKey)
      if (onVariationFocus) {
        onVariationFocus(variationKey)
      }
    },
    [onVariationFocus],
  )

  const isGlobalItem =
    useIsGlobalInventoryItem(inventory) ||
    isCvcCumulusRhapsodyIntegrationInventoryEnabled

  const handleClose = () => {
    if (onClose) {
      onClose(lastCreatedInventoryId, lastCreatedInventory)
    }
  }

  const setCloseAfterCreationOn = useCloseAfterCreation(
    handleClose,
    loadingSelector,
    true,
  )

  useEffect(() => {
    if (originalInventory && originalInventory.name) {
      setInventory({
        ...R.clone(originalInventory),
        name: `${originalInventory.name}_copy`,
      })
    }
  }, [originalInventory])

  useEffect(
    () => () => {
      setVariationsToCreate({})
      setSelectedVariationsMap({})
      focusOnVariation(undefined)
      setInventoryToCreate(undefined)
    },
    [],
  )

  const runVariationsRefill = () => {
    focusOnVariation(getVariationKey(Object.keys(selectedVariationsMap)[0]))
  }

  const prepareInventoryPayload = () => {
    const newInventory = {
      ...R.omit(['id'], inventory),
      ...(inventorySection.current?.get() || {}),
      ...(inventoryRestrictionSection.current?.getRestriction() || {}),
    }

    const updatedVariations = newInventory.variations
      ? newInventory.variations.map((variation: VariationType) =>
          R.omit(['id'], variation),
        )
      : null

    return { ...newInventory, variations: updatedVariations }
  }

  const runInitialVariationFilling = () => {
    if (inventorySection.current?.validate()) {
      const payload = prepareInventoryPayload()
      setInventoryToCreate(payload)
      focusOnVariation(getVariationKey(''))
    }
  }

  const runInventoryItemCloning = () => {
    if (inventorySection.current?.validate()) {
      const payload = prepareInventoryPayload()
      setCloseAfterCreationOn()
      dispatch(createInventory(payload))
    }
  }

  const onAddClick = () => {
    if (isGlobalItem) {
      runVariationsRefill()
    } else if (duplicateInventoryId) {
      runInventoryItemCloning()
    } else {
      runInitialVariationFilling()
    }
  }

  const onVariationFilled = (
    variation: VariationType,
    isClone?: boolean | Nil,
  ) => {
    const newVariationsToCreate: Record<string, VariationType[]> =
      addOrUpdateVariation(currentVariationKey, variation, variationsToCreate)

    const nextSelectedVariations = isClone
      ? insertVariation(
          {
            ...getVariation(currentVariationKey, selectedVariationsMap),
          } as VariationType,
          selectedVariationsMap,
          isFoodCatalogEnabled,
        )
      : selectedVariationsMap

    if (isClone) {
      setSelectedVariationsMap(nextSelectedVariations)
    }

    const nextVariationKey = getNextVariationKey(
      currentVariationKey,
      nextSelectedVariations,
    )

    if (nextVariationKey) {
      setVariationsToCreate(newVariationsToCreate)
      focusOnVariation(nextVariationKey)
    } else {
      setCloseAfterCreationOn()
      if (inventoryToCreate?.businessInventoryItemId) {
        dispatch(
          createVariations(
            inventoryToCreate.businessInventoryItemId,
            R.flatten(Object.values(newVariationsToCreate)),
          ),
        )
      } else if (inventoryToCreate?.id) {
        const globalIdKey = isFoodCatalogEnabled
          ? 'globalInventoryItemMappingId'
          : 'globalInventoryItemId'
        const flattenedVariations = R.flatten(
          Object.values(newVariationsToCreate),
        )

        if (isCvcCumulusRhapsodyIntegrationInventoryEnabled) {
          const item = inventoryToCreate as InventoryItem
          const globalId = item[globalIdKey]

          dispatch(
            createInventory({
              cumulusId: item.cumulusId,
              [globalIdKey]: globalId,
              variations: flattenedVariations,
            }),
          )
        } else {
          dispatch(
            createInventory({
              [globalIdKey]: inventoryToCreate.id,
              variations: flattenedVariations,
            }),
          )
        }
      } else {
        dispatch(
          createInventory({
            ...inventoryToCreate,
            variations: R.flatten(Object.values(newVariationsToCreate)),
          }),
        )
      }
    }
  }

  const onGoBack = () => {
    const prevVariationKey = getPrevVariationKey(
      currentVariationKey,
      selectedVariationsMap,
    )
    focusOnVariation(prevVariationKey)
  }

  const currentVariation =
    getVariation(currentVariationKey, variationsToCreate) ||
    getVariation(currentVariationKey, selectedVariationsMap)

  const isGlobalItemWithoutVariation =
    isGlobalItem && (!inventoryToCreate || R.isEmpty(selectedVariationsMap))

  useImperativeHandle(ref, () => ({
    confirmSave: () => {
      if (variationRef.current) {
        variationRef.current?.save()
      } else {
        onAddClick()
      }
    },
    hasUnsavedChanges: () =>
      variationRef.current?.getIsTouched() ||
      (isTouched && !isGlobalItemWithoutVariation),
  }))

  return currentVariationKey ? (
    <Grid container direction="column" onClick={() => setIsTouched(true)}>
      {isCvcCumulusRhapsodyIntegrationInventoryEnabled &&
      inventoryToCreate &&
      currentVariation ? (
        <ReadOnlyVariation
          inventory={inventoryToCreate}
          ref={variationRef}
          variation={currentVariation}
          onVariationFilled={onVariationFilled}
        />
      ) : (
        <Variation
          inventory={inventoryToCreate}
          nextVariation={getVariation(
            getNextVariationKey(currentVariationKey, selectedVariationsMap),
            selectedVariationsMap,
          )}
          ref={variationRef}
          singleVariationOnly={singleItemOnly}
          variation={currentVariation}
          variationIndex={getVariationIndex(
            currentVariationKey,
            selectedVariationsMap,
          )}
          variationsCount={getVariationsCount(selectedVariationsMap)}
          onBack={onGoBack}
          onVariationFilled={onVariationFilled}
        />
      )}
    </Grid>
  ) : (
    <Grid container item p={3} spacing={2} onClick={() => setIsTouched(true)}>
      <Grid item md={12} xs={12}>
        <Text variant="h2">
          {t('Admin:CATALOG.ADD_INVENTORY_ITEM.NEW_INVENTORY_ITEM')}
        </Text>
      </Grid>
      <CategorySelector
        category={inventory.categoryId}
        onCategorySet={(id: string) => {
          const subcategoryId =
            originalInventory?.categoryId === id
              ? originalInventory.subcategoryId
              : ''
          setInventory({ ...inventory, categoryId: id, subcategoryId })
        }}
      />
      {inventory.categoryId &&
        (isGlobalItem ? (
          <GlobalItemSelector
            disableMultiSelect={isCvcCumulusRhapsodyIntegrationInventoryEnabled}
            itemCategoryId={inventory.categoryId}
            selectedItem={inventoryToCreate}
            selectedVariationsMap={selectedVariationsMap}
            setSelectedItem={setInventoryToCreate}
            setSelectedVariationsMap={setSelectedVariationsMap}
            onEdit={onClose}
          />
        ) : (
          <>
            <Inventory dialog inventory={inventory} ref={inventorySection} />
            <InventoryRestriction
              inventory={inventory}
              ref={inventoryRestrictionSection}
            />
          </>
        ))}
      <Grid container item alignItems="center" mt={3} spacing={3} xs={12}>
        <Grid item>
          <ButtonWithLoader
            className={classes.saveButton}
            disabled={
              isLoading || !inventory.categoryId || isGlobalItemWithoutVariation
            }
            loading={isLoading}
            onClick={onAddClick}
          >
            {isGlobalItem ? t('Common:NEXT') : t('Common:ADD_ACTION')}
          </ButtonWithLoader>
        </Grid>
        {isGlobalItem && (
          <Grid item>
            <LetUsKnowLink />
          </Grid>
        )}
      </Grid>
    </Grid>
  )
})

export default AddInventoryItem
